const toNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

// Legacy-default convention: snapshots created before Phase 2 omit visit_reason and
// is_billable. Consumers must treat:
//   is_billable === undefined  → true  (billable; legacy invoices are all billable)
//   visit_reason === undefined → null  (no badge shown)
export const normalizeSnapshotItem = (item = {}) => {
  const quantity = toNumber(item.qty ?? item.quantity, 1) || 1;
  // Unpriced extra (RD-ROLE-005): a worker-logged "extra" the office hasn't
  // priced yet keeps price/total NULL — never coalesce to 0. Zero is a real
  // answer (a comped/free extra); a blank price is a question nobody has
  // answered, and rendering it as "$0.00" tells the customer it's free.
  const rawPrice = item.price ?? item.unit_price;
  const isUnpricedExtra = (item.type === 'extra') && (rawPrice === null || rawPrice === undefined || rawPrice === '');
  const price = isUnpricedExtra ? null : toNumber(rawPrice, 0);
  const total = price === null ? null : toNumber(item.total, quantity * price);

  return {
    type: item.type || 'item',
    name: item.name || item.description || 'Line Item',
    qty: quantity,
    price,
    total,
    visit_reason: item.visit_reason ?? null,
    is_billable: item.is_billable === false ? false : true,
  };
};

export const getInvoiceSnapshot = (invoice) => {
  if (!invoice?.snapshot) return null;

  if (typeof invoice.snapshot === 'string') {
    try {
      return JSON.parse(invoice.snapshot);
    } catch (error) {
      console.error('Unable to parse invoice snapshot', error);
      return null;
    }
  }

  if (typeof invoice.snapshot === 'object') {
    return invoice.snapshot;
  }

  return null;
};

/** Synthetic job id for manual invoices (no jobs table row). Legacy single-bucket manual snapshot. */
export const MANUAL_INVOICE_JOB_ID = '__manual_invoice__';

/** Synthetic job id for estimate-derived invoices (no real job rows). */
export const ESTIMATE_INVOICE_JOB_ID = '__estimate_invoice__';

const MANUAL_ENTRY_PREFIX = '__manual_entry__';

/**
 * True for legacy manual id or per–entry-date synthetic ids used in Slice 3+ manual invoices.
 * Does NOT match ESTIMATE_INVOICE_JOB_ID — estimate-derived invoices are a separate category.
 */
export function isManualSyntheticJobId(jobId) {
  if (jobId == null) return false;
  const s = String(jobId);
  return s === MANUAL_INVOICE_JOB_ID || s.startsWith(MANUAL_ENTRY_PREFIX);
}

/**
 * Stable synthetic job id for one manual dated entry (mirrors a “job” in snapshot/PDF grouping).
 * @param {number} entryIndex
 * @param {string} entryDate — YYYY-MM-DD
 */
export function buildManualEntryJobId(entryIndex, entryDate) {
  const d = String(entryDate || '').trim() || '1970-01-01';
  return `${MANUAL_ENTRY_PREFIX}${entryIndex}__${d}`;
}

/**
 * Parses the entry index out of a synthetic manual-entry job id
 * (`__manual_entry__N__YYYY-MM-DD`). Returns null for the legacy
 * single-bucket `__manual_invoice__` id, or anything that doesn't match —
 * callers should skip nulls when deriving a "next index" (see
 * useInvoicePreviewAddManualLine, which mints the next id from
 * max(existing indexes) + 1, not a count, to avoid colliding after a removal).
 * @param {string} jobId
 * @returns {number|null}
 */
export function getManualEntryIndex(jobId) {
  if (jobId == null) return null;
  const match = String(jobId).match(/^__manual_entry__(\d+)__/);
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) ? n : null;
}

/**
 * Canonical "this invoice's money was frozen by an estimate conversion" test.
 * `invoice.source_estimate_id` is the canonical marker — every converted row
 * carries it (useEstimateConvertToInvoice writes it on the insert), so this
 * also identifies rows that are ALREADY in the database. The snapshot's own
 * `invoice_origin` is the fallback for callers holding a snapshot but not the
 * full row.
 */
export const isEstimateDerivedInvoice = (invoice, snapshot = null) =>
  Boolean(invoice?.source_estimate_id) || snapshot?.invoice_origin === 'estimate';

/**
 * The FROZEN tax of an estimate-derived invoice, in dollars.
 *
 * buildConvertInvoiceSnapshotFromEstimate scales tax by the partial factor at
 * convert time (a 50% deposit's tax is HALF the estimate's tax) while leaving
 * `snapshot.invoice.subtotal` as the FULL unscaled sum. Every consumer that
 * recomputes tax does it as `(subtotal - credit) * rate`, which on a 50%
 * deposit roughly DOUBLES the tax line. So tax gets the same contract as
 * `partial_adjustment`: preserve it, never recompute it.
 *
 * Returns null when the snapshot has no invoice block, so callers fall back to
 * their ordinary recompute.
 */
export const getFrozenEstimateTax = (snapshot) => {
  const snapshotInvoice = snapshot?.invoice;
  if (!snapshotInvoice) return null;
  return {
    taxAmount: toNumber(snapshotInvoice.tax_amount, 0),
    taxRate: toNumber(snapshotInvoice.tax_rate, 0),
  };
};

export const buildInvoiceSnapshot = ({
  invoice,
  customer,
  jobs,
  jobDetails,
  creditAmount = 0,
  creditDescription = '',
  notes = '',
  taxAmount = 0,
  taxRate = 0,
  taxState = '',
  invoiceOrigin = 'automatic',
}) => {
  const snapshotJobs = (jobs || []).map((job) => {
    const details = jobDetails?.[job.id] || {};
    const visitReason = job.visit_reason ?? null;
    const rawItems = details.items || [
      ...(details.services || []).map((item) => ({ ...item, type: 'service' })),
      ...(details.chemicals || []).map((item) => ({ ...item, type: 'chemical' })),
      ...(details.extras || []).map((item) => ({ ...item, type: 'extra' })),
    ];
    // Inject visit_reason from the job into each item at serialization time.
    // is_billable is stamped from tenant billing rules before buildInvoiceSnapshot is called.
    const items = rawItems.map((raw) =>
      normalizeSnapshotItem({ visit_reason: visitReason, ...raw })
    );
    // Cent-round each accumulator to match the Postgres trigger's
    // ROUND(numeric, 2) so JS float drift can't disagree with the DB by a
    // penny across many line items.
    // Only billable items (is_billable !== false) contribute to the total.
    // An unpriced extra's `total` is null (RD-ROLE-005) — `?? 0` so the sum
    // never NaNs and the office still sees the priced lines' subtotal while
    // the unpriced one waits to be priced (surfaced via the pill, not here).
    const fromLineItems = Math.round(
      items.reduce((sum, item) => sum + (item.is_billable !== false ? (item.total ?? 0) : 0), 0) * 100
    ) / 100;
    const storedJobTotal = toNumber(job.total, null);
    // Prefer line-item sum when we have items so snapshot/PDF match actual line items (job.total can be stale)
    const jobTotal = items.length > 0
      ? fromLineItems
      : (Number.isFinite(storedJobTotal) && storedJobTotal >= 0 ? storedJobTotal : fromLineItems);

    return {
      id: job.id,
      date: job.date,
      total: jobTotal,
      service_address: job.service_address || null,
      // Persist the frozen billable status + reason so useInvoicePreview's
      // editable-mode recompute can use the freeze-first contract
      // (`job.is_billable_default ?? resolveBillableDefault(...)`). Without
      // this, the preview falls back to the current tenant rule, which can
      // drift away from the snapshot's stored item-level is_billable after a
      // rule change.
      visit_reason: visitReason,
      is_billable_default: job.is_billable_default ?? null,
      items,
    };
  });

  const subtotal = Math.round(snapshotJobs.reduce((sum, job) => sum + job.total, 0) * 100) / 100;
  const appliedCredit = toNumber(creditAmount, 0);

  // Carry forward the estimate-partial deduction + estimate identity from the
  // invoice's EXISTING snapshot. buildConvertInvoiceSnapshotFromEstimate stores
  // the FULL unscaled sum as the synthetic job's total and expresses the
  // reduction ONLY as `snapshot.partial_adjustment` (fixed cents), with the
  // estimate number at `snapshot.source_estimate_number`. This builder
  // recomputes subtotal from jobs[].total, so before this it dropped both on a
  // plain re-save: a 50% deposit re-inflated to the full estimate amount, the
  // deposit line + contract memo disappeared from the PDF (they require BOTH
  // fields — see buildInvoiceDocumentProps `isPartialEstimate`), and the
  // estimate then read as fully invoiced so the second payment could never be
  // billed. Safe to carry unchanged: an estimate-derived invoice has a LOCKED
  // scope (add/remove job and add/edit/remove manual line all bail on
  // `invoice.source_estimate_id`), so the subtotal this is subtracted from
  // cannot move under the user and the fixed cents amount cannot go stale.
  const priorSnapshot = getInvoiceSnapshot(invoice);
  const partialAdjustment = priorSnapshot?.partial_adjustment || null;
  const sourceEstimateNumber = priorSnapshot?.source_estimate_number ?? null;
  // Money stays in integer cents until the single final divide — no float drift.
  const partialAdjustmentCents = Math.max(0, Math.round(toNumber(partialAdjustment?.amount_cents, 0)));

  // Tax is frozen the same way (see getFrozenEstimateTax): convert already
  // scaled it by the partial factor, while `subtotal` above is the FULL
  // unscaled sum. Every caller that hands us a tax figure recomputes it as
  // (subtotal - credit) * rate — on a 50% deposit that roughly DOUBLES the tax
  // line — so on an estimate-derived invoice we re-emit what convert stored and
  // ignore the recomputed value. Non-estimate invoices are untouched.
  const frozenEstimateTax = isEstimateDerivedInvoice(invoice, priorSnapshot)
    ? getFrozenEstimateTax(priorSnapshot)
    : null;
  const appliedTax = frozenEstimateTax ? frozenEstimateTax.taxAmount : toNumber(taxAmount, 0);
  const appliedTaxRate = frozenEstimateTax ? frozenEstimateTax.taxRate : (taxRate || 0);

  const rawBalanceCents = Math.round((subtotal - appliedCredit + appliedTax) * 100);
  // Clamp only in the adjustment branch so the no-adjustment path stays
  // byte-identical to its previous behavior; the clamp mirrors
  // computeInvoiceBalanceDue, which the preview uses for the displayed total.
  const balanceDue = (partialAdjustmentCents > 0
    ? Math.max(0, rawBalanceCents - partialAdjustmentCents)
    : rawBalanceCents) / 100;

  return {
    version: 1,
    invoice_origin: invoiceOrigin,
    created_at: new Date().toISOString(),
    ...(sourceEstimateNumber != null ? { source_estimate_number: sourceEstimateNumber } : {}),
    invoice: {
      customer_id: invoice.customer_id,
      date_from: invoice.date_from,
      date_to: invoice.date_to,
      subtotal,
      credit_amount: appliedCredit,
      credit_description: creditDescription || '',
      tax_amount: appliedTax,
      tax_rate: appliedTaxRate,
      // tax_state is a DISPLAY label (the rate's jurisdiction), not money, so it
      // is not part of the estimate freeze — convert stores '' and the org value
      // is what keeps the preview's tax control rendering.
      tax_state: taxState || '',
      notes: notes || '',
      balance_due: balanceDue,
      total: balanceDue,
      status: invoice.status || 'draft',
    },
    customer: customer
      ? {
          id: customer.id,
          full_name: customer.full_name || '',
          billing_address: customer.billing_address || '',
          service_address: customer.service_address || '',
          phone: customer.phone || '',
          email: customer.email || '',
        }
      : null,
    jobs: snapshotJobs,
    // Re-emitted verbatim (same shape/placement as the convert builder) so a
    // re-save never silently drops the partial-billing deduction.
    ...(partialAdjustment ? { partial_adjustment: partialAdjustment } : {}),
  };
};

export const getSnapshotJobs = (snapshot) => {
  return (snapshot?.jobs || []).map((job) => ({
    id: job.id,
    date: job.date,
    total: toNumber(job.total, 0),
    service_address: job.service_address || null,
    // Expose the frozen freeze-on-creation fields so consumers (useInvoicePreview)
    // can apply the freeze-first contract during editable recomputes.
    visit_reason: job.visit_reason ?? null,
    is_billable_default: job.is_billable_default ?? null,
  }));
};

export const getSnapshotJobDetails = (snapshot) => {
  return (snapshot?.jobs || []).reduce((accumulator, job) => {
    accumulator[job.id] = {
      items: (job.items || []).map(normalizeSnapshotItem),
    };
    return accumulator;
  }, {});
};